/**
 * Actor and credential layer over Link topology.
 *
 * A Link can describe a person, profile, public key, policy, or proof, but issuance needs a narrower concept:
 * the accountable subject whose currency is being issued. That subject is modeled here as [Actor].
 *
 * Credentials are deliberately one layer lower than actors. A public key is a way to prove authorization; it is not
 * the issuer itself. Keeping credential protocols explicit lets Ed25519, post-quantum signatures, threshold control,
 * and future recovery policies coexist without changing the economic identity of an actor.
 */

package com.oasisfeng.invaluable.actor

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.data.AnchoredLinkTopology
import com.oasisfeng.invaluable.data.AnchoredViewTopology
import com.oasisfeng.invaluable.data.LinkTopology
import com.oasisfeng.invaluable.data.LinkView
import com.oasisfeng.invaluable.then
import kotlinx.io.bytestring.ByteString
import kotlin.jvm.JvmInline

/**
 * Actor topology is the actor dimension anchored to a concrete [Credential].
 *
 * Multiple profiles, names, or public appearances should be modeled above this layer;
 * the actor is only the accountable issuer whose commitments can be attributed to one credential topology.
 */
@JvmInline value class Actor private constructor(override val link: NestedLink) : LinkView {
    companion object : AnchoredViewTopology<Credential, Actor>(::Actor, Dimensions.ACTOR(), targetTopology = Credential)
    override fun toString(): String = link.toString()
}

/** Graph-native authorization material, such as a public key or policy document under a credential protocol. */
@JvmInline value class Credential private constructor(override val link: NestedLink) : LinkView {
    companion object : LinkTopology<Credential>(::Credential) {
        /** Build credential material under the graph-native [protocol] that knows how to verify it. */
        operator fun invoke(protocol: CredentialProtocol, material: NestedLink)
        = Credential(protocol.link.then(material))
        /** A credential is any non-root material Link under a valid [CredentialProtocol]. */
        override fun NestedLink.matchesTopology()
        = CredentialProtocol.matchNode(parent().nestedOrNull()) && nestedTargetOrNull() != null
    }

    override fun toString(): String = link.toString()
}

/**
 * A credential has a graph-native protocol parent and optional off-graph verification logic elsewhere.
 *
 * The model layer only defines the structural protocol. Implementations belong in adapter modules.
 *
 * ## Different Protocol Representations
 *
 * Grounded in the Ontology Genesis Charter, a credential protocol is persistent topology rooted at
 * `DATA.into(ACTOR)`. Its target may range from an external identifier to a fully graph-native verification law:
 *
 * ### 1. Text-Based: Semantic Names
 * Protocols are defined in the **Meaning Dimension** by attaching a textual signifier to a semantic root.
 * Example: `credentialAnchor().then(protocol("ed25519-spki.v1"))`.
 * - *Limitation:* It introduces cultural/language conventions (ASCII/UTF-8) and relies on subjective registry mapping.
 *
 * ### 2. OID-Based: External Numeric References
 * Externally registered Object Identifiers (OIDs) are represented in the **Data Dimension** as a numeric arc chain
 * (e.g. OID `1.3.101.112` for Ed25519).
 * Example: `credentialAnchor().then(dataAnchor().then(1).then(3).then(101).then(112))`.
 * - *Boundary:* The numeric topology avoids text encoding and local name collisions, but the external registry and
 *   referenced specification still assign its meaning. It is an explicit interoperability compromise, not the
 *   algorithm's complete ontological definition.
 *
 * ### 3. AST/Instruction-Based: Executable Calculus Topology (Future)
 * The protocol identity itself is the **Abstract Syntax Tree (AST)** of the verification logic (e.g., the
 * twisted Edwards curve verification formula `8*S*B = 8*R + 8*H(R,A,M)*A` expressed as a combination of modular
 * arithmetic and point addition operators).
 * - *Advantage:* The graph contains not just an identifier/label, but the mathematical law of verification itself.
 *   Verification is executed by reducing the AST (using Graph Reduction VM / Term Rewriting System). It requires
 *   no host-code changes or hard-coded algorithms, enabling zero-hard-fork evolution.
 */
@JvmInline value class CredentialProtocol private constructor(override val link: NestedLink) : LinkView {
    companion object : AnchoredLinkTopology<CredentialProtocol>(
        ctor = ::CredentialProtocol, anchor = Dimensions.DATA.into(Dimensions.ACTOR).link)
    override fun toString(): String = link.toString()
}

/** Compact proof bytes for a credential protocol, such as a detached signature. */
data class CredentialProof(val protocol: CredentialProtocol, val payload: ByteString)

/** Return a credential's protocol-specific material if it belongs to [protocol]. */
fun credentialMaterialOrNull(credential: Credential, protocol: CredentialProtocol): NestedLink? {
    val link = credential.link
    if (! link.isChildOf(protocol.link)) return null
    return link.nestedTargetOrNull()
}
